import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, MessageCircle, Megaphone, Inbox, Sparkles } from "lucide-react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../ui/popover";
import { fetchNotifications, markNotificationsRead } from "../../lib/studioApi";
import { formatDistanceToNow } from "date-fns";
import { toast } from "sonner";

function safeTime(iso) {
  try {
    return formatDistanceToNow(new Date(iso), { addSuffix: true });
  } catch {
    return "";
  }
}

export default function NotificationBell() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [data, setData] = useState({ unread: { total: 0, posts: 0, messages: 0, perks: 0 }, items: [] });

  useEffect(() => {
    const tick = () => {
      fetchNotifications()
        .then(setData)
        .catch((e) => console.warn("notifications poll failed:", e?.message));
    };
    tick();
    const id = setInterval(tick, 8000);
    return () => clearInterval(id);
  }, []);

  const unread = data.unread?.total || 0;

  const markAllRead = async () => {
    try {
      await markNotificationsRead();
      setData({ unread: { total: 0, posts: 0, messages: 0, perks: 0 }, items: [] });
    } catch (e) {
      console.error("mark all read failed:", e);
      toast.error("Couldn't mark all as read");
    }
  };

  const goto = (item) => {
    setOpen(false);
    if (item.kind === "message") {
      navigate("/studio/messages");
    } else if (item.kind === "perk") {
      navigate("/studio/perks");
    } else {
      navigate("/studio");
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid="studio-notification-bell"
          className="relative flex items-center gap-2 px-3 md:px-4 py-2 rounded-lg text-sm font-medium text-white/70 hover:bg-white/5 hover:text-white transition-colors"
          aria-label="Notifications"
        >
          <Bell className="w-4 h-4" />
          <span className="hidden sm:inline">Alerts</span>
          {unread > 0 && (
            <span
              data-testid="studio-bell-badge"
              className="absolute -top-0.5 -right-0.5 bg-red-500 text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] px-1 flex items-center justify-center border border-[#10162a]"
            >
              {unread > 9 ? "9+" : unread}
            </span>
          )}
        </button>
      </PopoverTrigger>

      <PopoverContent
        align="end"
        sideOffset={8}
        className="w-80 md:w-96 p-0 bg-[#10162a] border-white/10 text-white shadow-[0_20px_60px_rgba(0,0,0,0.5)] overflow-hidden"
        data-testid="studio-notification-popover"
      >
        <div className="px-4 py-3 border-b border-white/10 flex items-center justify-between">
          <div>
            <div className="text-sm font-semibold">Notifications</div>
            <div className="text-[11px] text-white/40">
              {unread > 0
                ? `${data.unread.posts} update${data.unread.posts === 1 ? "" : "s"} · ${data.unread.messages} message${data.unread.messages === 1 ? "" : "s"}${data.unread.perks ? ` · ${data.unread.perks} perk${data.unread.perks === 1 ? "" : "s"}` : ""}`
                : "You're all caught up"}
            </div>
          </div>
          {unread > 0 && (
            <button
              type="button"
              data-testid="studio-bell-mark-all-read"
              onClick={markAllRead}
              className="text-xs text-paul-yellow font-semibold hover:underline"
            >
              Mark all read
            </button>
          )}
        </div>

        <div className="max-h-80 overflow-y-auto">
          {data.items.length === 0 ? (
            <div className="text-center py-10 text-white/40">
              <Inbox className="w-8 h-8 mx-auto mb-2 text-white/20" />
              <p className="text-xs">No new notifications</p>
            </div>
          ) : (
            <ul>
              {data.items.map((it) => {
                const isMsg = it.kind === "message";
                const isPerk = it.kind === "perk";
                const Icon = isMsg
                  ? MessageCircle
                  : isPerk
                    ? Sparkles
                    : it.scope === "global"
                      ? Megaphone
                      : Inbox;
                return (
                  <li key={`${it.kind}-${it.id}`}>
                    <button
                      type="button"
                      data-testid={`studio-bell-item-${it.kind}-${it.id}`}
                      onClick={() => goto(it)}
                      className="w-full text-left px-4 py-3 border-b border-white/5 hover:bg-white/5 flex gap-3 transition-colors"
                    >
                      <div
                        className={`w-9 h-9 rounded-full flex-shrink-0 flex items-center justify-center ${
                          isMsg
                            ? "bg-paul-yellow/15 text-paul-yellow"
                            : isPerk
                              ? "bg-paul-yellow/15 text-paul-yellow"
                              : it.scope === "global"
                                ? "bg-blue-500/15 text-blue-300"
                                : "bg-emerald-500/15 text-emerald-300"
                        }`}
                      >
                        <Icon className="w-4 h-4" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-semibold text-white truncate">
                          {it.title}
                        </div>
                        <div className="text-xs text-white/60 truncate">
                          {it.preview}
                        </div>
                        <div className="text-[11px] text-white/40 mt-0.5">
                          {safeTime(it.createdAt)}
                        </div>
                      </div>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
